import { useState, useEffect } from "react";
import { Modal, Form, Input, Select, DatePicker, Switch, Button, Row, Col, message } from "antd";
import { ArrowLeftOutlined, CloseOutlined, UserOutlined, BankOutlined } from "@ant-design/icons";
import useSettingsDropdown from "../../hooks/useSettingsDropdown";
import { createRider, updateRider } from "../../services/riders.service";
import dayjs from "dayjs";

// Onboarding formats — keep in step with EZYGO_BACKEND Rider/constants.js.
const PHONE_RULE = {
  pattern: /^[6-9][0-9]{7,12}$/,
  message: "Must be 8–13 digits starting with 6, 7, 8 or 9",
};
const CIVIL_ID_RULE = { pattern: /^[0-9]{1,12}$/, message: "Civil ID must be up to 12 digits" };
const IBAN_RULE = { pattern: /^OM[0-9]{1,25}$/, message: "IBAN must be OM followed by up to 25 digits" };

export default function RiderFormModal({ visible, rider, onClose }) {
  const [form] = Form.useForm();
  const [activeStep, setActiveStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [selectedCity, setSelectedCity] = useState(null);

  const isEditMode = !!rider;

  // Settings dropdowns
  const { options: nationalityOptions, loading: nationalityLoading } = useSettingsDropdown("nationalities");
  const { options: designationOptions, loading: designationLoading } = useSettingsDropdown("designations", {
    filter: (item) => item.designationType === "rider",
  });
  const { options: projectOptions, loading: projectLoading } = useSettingsDropdown("projects");
  
  // Load ALL zones (we'll filter on frontend based on selected city)
  const { options: allZoneOptions, loading: zoneLoading, data: allZones } = useSettingsDropdown("city-zones", {
    filter: (item) => item.entityType === "zone",
  });
  
  const { options: cityOptions, loading: cityLoading } = useSettingsDropdown("city-zones", {
    filter: (item) => item.entityType === "city",
  });
  
  const { options: salaryOptions, loading: salaryLoading } = useSettingsDropdown("salary-schemes");
  const { options: bankOptions, loading: bankLoading } = useSettingsDropdown("bank-details");
  const { options: fleetOptions, loading: fleetLoading } = useSettingsDropdown("fleet-vehicle-types", {
    filter: (item) => item.category === "fleet",
  });

  // Filter zones based on selected city
  const zoneOptions = selectedCity
    ? allZoneOptions.filter((zone) => {
        const zoneData = allZones?.find((z) => z._id === zone.value);
        return zoneData?.parentCity?.toString() === selectedCity?.toString();
      })
    : [];

  useEffect(() => {
    if (visible && rider) {
      setActiveStep(1); // Reset to step 1 when modal opens
      setSelectedCity(rider.city?._id || null); // Set selected city for zone filtering
      form.setFieldsValue({
        fullName: rider.fullName,
        nationality: rider.nationality?._id,
        mobileNumber: rider.mobileNumber,
        whatsappNumber: rider.whatsappNumber,
        emailAddress: rider.emailAddress,
        designation: rider.designation?._id,
        project: rider.project?._id,
        zone: rider.zone?._id,
        city: rider.city?._id,
        fleetType: rider.fleetType?._id,
        civilId: rider.civilId,
        externalRiderId: rider.externalRiderId || undefined,
        dateOfJoining: rider.dateOfJoining ? dayjs(rider.dateOfJoining) : null,
        status: rider.status === "active",
        salaryScheme: rider.salaryScheme?._id,
        bankName: rider.bankName?._id,
        bankAccountNo: rider.bankAccountNo,
        iban: rider.iban,
      });
    } else if (visible) {
      setActiveStep(1); // Reset to step 1 for new rider
      setSelectedCity(null); // Reset city selection
      form.resetFields();
      form.setFieldsValue({ status: true });
    }
  }, [visible, rider, form]);

  // Step-specific field lists
  const step1Fields = [
    "fullName", "nationality", "mobileNumber", "whatsappNumber", "emailAddress",
    "designation", "project", "zone", "city", "fleetType", "civilId", "externalRiderId",
    "dateOfJoining", "status",
  ];
  const step2Fields = ["salaryScheme", "bankName", "bankAccountNo", "iban"];

  const handleNext = async () => {
    try {
      // Validate only Step 1 fields
      await form.validateFields(step1Fields);
      setActiveStep(2);
    } catch (error) {
      message.error("Please fill all required fields in Basic Info");
    }
  };

  const handleBack = () => {
    setActiveStep(1);
  };

  const handleSubmit = async () => {
    try {
      // Validate all fields
      const values = await form.validateFields();
      
      console.log("Form values before submission:", values);
      
      setSubmitting(true);

      const payload = {
        ...values,
        dateOfJoining: values.dateOfJoining ? values.dateOfJoining.format("YYYY-MM-DD") : null,
        status: values.status ? "active" : "inactive",
        externalRiderId: values.externalRiderId ? String(values.externalRiderId).trim() : "",
      };

      console.log("Payload being sent:", payload);

      if (isEditMode) {
        await updateRider(rider._id, payload);
        message.success("Rider updated successfully");
      } else {
        await createRider(payload);
        message.success("Rider created successfully");
      }

      onClose(true);
    } catch (error) {
      console.error("Form submission error:", error);
      if (error.errorFields) {
        message.error("Please fill all required fields");
        console.log("Error fields:", error.errorFields);
      } else {
        message.error(isEditMode ? "Failed to update rider" : "Failed to create rider");
        console.error("API error:", error);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCityChange = (cityId) => {
    setSelectedCity(cityId);
    // Clear zone selection when city changes
    form.setFieldsValue({ zone: undefined });
  };

  const handleReset = () => {
    form.resetFields();
    form.setFieldsValue({ status: true });
    setActiveStep(1);
    setSelectedCity(null);
  };

  const steps = [
    { key: 1, label: "Basic Info", icon: <UserOutlined /> },
    { key: 2, label: "Bank Details", icon: <BankOutlined /> },
  ];

  const renderStepContent = () => {
    return (
      <>
        {/* Step 1: Basic Info */}
        <div style={{ display: activeStep === 1 ? 'block' : 'none' }}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
            <Form.Item
              name="fullName"
              label="Full Name"
              rules={[
                { required: true, message: "Full name is required" },
                { min: 2, max: 100, message: "Full name must be between 2 and 100 characters" },
              ]}
            >
              <Input placeholder="Enter full name" maxLength={100} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="nationality"
              label="Nationality"
              rules={[{ required: true, message: "Nationality is required" }]}
            >
              <Select
                placeholder="Select nationality"
                options={nationalityOptions}
                loading={nationalityLoading}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="mobileNumber"
              label="Mobile Number"
              rules={[
                { required: true, message: "Mobile number is required" },
                PHONE_RULE,
              ]}
            >
              <Input placeholder="e.g., 91234567" maxLength={13} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="whatsappNumber"
              label="WhatsApp Number"
              rules={[PHONE_RULE]}
            >
              <Input placeholder="e.g., 91234567" maxLength={13} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="emailAddress"
              label="Email Address"
              rules={[{ type: "email", message: "Please enter a valid email address" }]}
            >
              <Input placeholder="e.g., rider@example.com" type="email" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="designation"
              label="Designation"
              rules={[{ required: true, message: "Designation is required" }]}
            >
              <Select
                placeholder="Select designation"
                options={designationOptions}
                loading={designationLoading}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="project"
              label="Project"
              rules={[{ required: true, message: "Project is required" }]}
            >
              <Select
                placeholder="Select project"
                options={projectOptions}
                loading={projectLoading}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="city" label="City" rules={[{ required: true, message: "City is required" }]}>
              <Select
                placeholder="Select city first"
                options={cityOptions}
                loading={cityLoading}
                showSearch
                optionFilterProp="label"
                onChange={handleCityChange}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="zone" label="Zone" rules={[{ required: true, message: "Zone is required" }]}>
              <Select
                placeholder={selectedCity ? "Select zone" : "Select city first"}
                options={zoneOptions}
                loading={zoneLoading}
                showSearch
                optionFilterProp="label"
                disabled={!selectedCity}
                notFoundContent={selectedCity ? "No zones found for this city" : "Please select a city first"}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="fleetType"
              label="Fleet Type"
              rules={[{ required: true, message: "Fleet type is required" }]}
            >
              <Select
                placeholder="Select fleet type"
                options={fleetOptions}
                loading={fleetLoading}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="civilId"
              label="Civil ID / Resident ID"
              rules={[
                { required: true, message: "Civil ID is required" },
                CIVIL_ID_RULE,
              ]}
            >
              <Input placeholder="e.g., 12345678" maxLength={12} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="externalRiderId"
              label="External Rider ID"
              tooltip="Aggregator / platform ID (e.g. Talabat Rider ID). Used to match daily transaction uploads."
              rules={[
                { max: 64, message: "External Rider ID must be at most 64 characters" },
              ]}
            >
              <Input placeholder="e.g., Talabat Rider ID" maxLength={64} allowClear />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="dateOfJoining"
              label="Date of Joining"
              rules={[
                { required: true, message: "Date of joining is required" },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    const today = dayjs();
                    const tenYearsAgo = today.subtract(10, 'year');
                    if (value.isAfter(today)) {
                      return Promise.reject(new Error('Date cannot be in the future'));
                    }
                    if (value.isBefore(tenYearsAgo)) {
                      return Promise.reject(new Error('Date cannot be more than 10 years ago'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <DatePicker 
                style={{ width: "100%" }} 
                format="DD/MM/YYYY" 
                placeholder="Select date"
                disabledDate={(current) => current && current > dayjs().endOf('day')}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="status" label="Status" valuePropName="checked">
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>
          </Col>
          </Row>
        </div>

        {/* Step 2: Bank Details */}
        <div style={{ display: activeStep === 2 ? 'block' : 'none' }}>
          <Row gutter={[16, 16]}>
          <Col span={12}>
            <Form.Item
              name="salaryScheme"
              label="Salary Scheme"
              rules={[{ required: true, message: "Salary scheme is required" }]}
            >
              <Select
                placeholder="Select salary scheme"
                options={salaryOptions}
                loading={salaryLoading}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="bankName"
              label="Bank Name"
              rules={[{ required: true, message: "Bank name is required" }]}
            >
              <Select
                placeholder="Select bank"
                options={bankOptions}
                loading={bankLoading}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="bankAccountNo"
              label="Account Number"
              rules={[
                { required: true, message: "Account number is required" },
                { pattern: /^[0-9]{10,20}$/, message: "Account number must be 10-20 digits" },
              ]}
            >
              <Input placeholder="e.g., 1234567890" maxLength={20} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="iban"
              label="IBAN"
              rules={[IBAN_RULE]}
            >
              <Input placeholder="e.g., OM03XXXXXXXXXXXXXXXXX" maxLength={27} style={{ textTransform: 'uppercase' }} />
            </Form.Item>
          </Col>
          </Row>
        </div>
      </>
    );
  };

  return (
    <Modal
      open={visible}
      onCancel={() => onClose(false)}
      width={1024}
      footer={null}
      className="rider-form-modal"
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {isEditMode && <ArrowLeftOutlined style={{ fontSize: 16, cursor: "pointer" }} onClick={() => onClose(false)} />}
          <span>{isEditMode ? "Edit Rider" : "Add Rider"}</span>
        </div>
      }
      closeIcon={<CloseOutlined />}
      destroyOnClose
    >
      <div className="rider-form-steps">
        {/* Sidebar */}
        <div className="rider-form-sidebar">
          {steps.map((step) => (
            <div
              key={step.key}
              className={`rider-form-step-item ${activeStep === step.key ? "active" : ""} ${
                step.disabled ? "disabled" : ""
              }`}
              style={{ cursor: step.disabled ? "not-allowed" : "default" }}
            >
              <div className="rider-form-step-icon">{step.icon}</div>
              <div className="rider-form-step-label">{step.label}</div>
            </div>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1 }}>
          <div className="rider-form-content">
            <Form form={form} layout="vertical" requiredMark={false}>
              {renderStepContent()}
            </Form>
          </div>

          {/* Actions */}
          <div className="rider-form-actions">
            {activeStep === 1 ? (
              <>
                <Button onClick={handleReset} disabled={submitting}>
                  Reset
                </Button>
                <Button type="primary" onClick={handleNext}>
                  Next
                </Button>
              </>
            ) : (
              <>
                <Button onClick={handleBack} disabled={submitting}>
                  Back
                </Button>
                <Button type="primary" onClick={handleSubmit} loading={submitting}>
                  {isEditMode ? "Update" : "Save"}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
